import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import Button from 'reactstrap/lib/Button';
import classnames from 'classnames';
import { NAMES_TO_COLOR_CODES } from 'client/site-modules/infographics/constants/most-popular-colors';
import { Link } from 'site-modules/shared/components/link/link';

import './state-card-colors-content.scss';

export const MOST_POPULAR_VIEW = 'most_popular';
export const LEAST_POPULAR_VIEW = 'least_popular';

// The bars look odd since they’re such small percentages and so we rescale the bars so that
// it’s on a scale out of 7%. For example, if the data point is 3.5% it
// would take up 50% of the total bar since it’s out of 7%.
const LEAST_POPULAR_PERCENT_BASE = 7;

function StateCardColor({ color, colorName, percent, percentTitle = '' }) {
  return (
    <div className="state-card-color d-flex align-items-center size-16 text-gray-darker mb-1_25">
      <div
        className="color-block me-0_5"
        style={{
          flexBasis: percent,
          height: '16px',
          backgroundColor: NAMES_TO_COLOR_CODES[color],
          border: color === 'white' ? '0.25px solid #555555' : 'none',
        }}
      />
      {percentTitle || percent} {colorName}
    </div>
  );
}

StateCardColor.propTypes = {
  color: PropTypes.string.isRequired,
  colorName: PropTypes.string.isRequired,
  percent: PropTypes.string.isRequired,
  percentTitle: PropTypes.string,
};

function ViewSelectBtn({ value, text, onClick, isSelected = false }) {
  return (
    <Button
      color="link"
      className={classnames(
        'view-select-btn text-capitalize p-0 size-16',
        {
          selected: isSelected,
        },
        isSelected ? 'text-primary-darker' : 'text-gray-darker'
      )}
      onClick={onClick}
      data-testid={value}
    >
      <span className="pb-0_5">{text}</span>
    </Button>
  );
}

ViewSelectBtn.propTypes = {
  value: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  isSelected: PropTypes.bool,
};

export function StateCardColorsContent({ stateData = null }) {
  const [selectedView, setSelectedView] = useState(MOST_POPULAR_VIEW);

  const setMostPopularView = useCallback(() => {
    setSelectedView(MOST_POPULAR_VIEW);
  }, [setSelectedView]);

  const setLeastPopularView = useCallback(() => {
    setSelectedView(LEAST_POPULAR_VIEW);
  }, [setSelectedView]);

  if (!stateData) return null;

  const {
    mostPopularColor1,
    mostPopularColor2,
    mostPopularColor3,
    mostPopularColor4,
    mostPopularColor5,
    mostPopularColorPercent1,
    mostPopularColorPercent2,
    mostPopularColorPercent3,
    mostPopularColorPercent4,
    mostPopularColorPercent5,
    leastPopularColor1,
    leastPopularColor2,
    leastPopularColor3,
    leastPopularColor4,
    leastPopularColor5,
    leastPopularColorPercent1,
    leastPopularColorPercent2,
    leastPopularColorPercent3,
    leastPopularColorPercent4,
    leastPopularColorPercent5,
    mostPopularCar,
    mostPopularCarURL,
  } = stateData;

  const mostPopularGrouped = [
    [mostPopularColor1, mostPopularColorPercent1],
    [mostPopularColor2, mostPopularColorPercent2],
    [mostPopularColor3, mostPopularColorPercent3],
    [mostPopularColor4, mostPopularColorPercent4],
    [mostPopularColor5, mostPopularColorPercent5],
  ];

  const leasePopularGrouped = [
    [leastPopularColor5, leastPopularColorPercent5],
    [leastPopularColor4, leastPopularColorPercent4],
    [leastPopularColor3, leastPopularColorPercent3],
    [leastPopularColor2, leastPopularColorPercent2],
    [leastPopularColor1, leastPopularColorPercent1],
  ];

  const structuredStateData = {
    mostPopular: mostPopularGrouped.map(([colorName, percent]) => ({ colorName, percent })),
    leastPopular: leasePopularGrouped.map(([colorName, percent]) => ({
      colorName,
      percent: `${((+percent.replace('%', '') * 100) / LEAST_POPULAR_PERCENT_BASE).toFixed(1)}%`,
      percentTitle: percent,
    })),
  };

  const dataToRender =
    selectedView === MOST_POPULAR_VIEW ? structuredStateData.mostPopular : structuredStateData.leastPopular;

  return (
    <div className="state-card-colors-content">
      <div className="d-flex align-items-center mb-1_5">
        <ViewSelectBtn
          value={MOST_POPULAR_VIEW}
          text="Most Popular"
          onClick={setMostPopularView}
          isSelected={selectedView === MOST_POPULAR_VIEW}
        />
        <div className="px-0_25">|</div>
        <ViewSelectBtn
          value={LEAST_POPULAR_VIEW}
          text="Least Popular"
          onClick={setLeastPopularView}
          isSelected={selectedView === LEAST_POPULAR_VIEW}
        />
      </div>
      {dataToRender.map(row => (
        <StateCardColor key={row.colorName} color={row.colorName.toLowerCase().replace(/\s/, '-')} {...row} />
      ))}
      <p className="size-16 text-gray-darker mb-0">
        Most popular car:{' '}
        <Link to={mostPopularCarURL} className="popular-car-link text-primary-darker">
          {mostPopularCar}
        </Link>
      </p>
    </div>
  );
}

StateCardColorsContent.propTypes = {
  stateData: PropTypes.shape({}),
};
